/**
 * 예배 안내 페이지
 */

import React from 'react';
import styled from '@emotion/styled';
import { MainLayout } from '../../../../shared/components';
import { theme } from '../../../../shared/styles/theme';
import { mediaQueries } from '../../../../shared/constants/breakpoints';
import { CHURCH_INFO } from '../../data/churchInfo';

interface WorshipService {
  name: string;
  time: string;
  location: string;
}

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  font-family: ${theme.fonts.heading};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.xxxl};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const WorshipTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${theme.colors.background};
  box-shadow: ${theme.shadows.md};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.sm};
  }
`;

const TableHead = styled.thead`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
`;

const TableRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border};
  }

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const TableHeader = styled.th`
  padding: ${theme.spacing.lg} ${theme.spacing.md};
  text-align: left;
  font-weight: 700;
  font-size: ${theme.fontSizes.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.md} ${theme.spacing.sm};
    font-size: ${theme.fontSizes.md};
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg} ${theme.spacing.md};
  color: ${theme.colors.text};
  font-size: ${theme.fontSizes.md};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.md} ${theme.spacing.sm};
    font-size: ${theme.fontSizes.sm};
  }
`;

const WorshipName = styled.span`
  font-weight: 700;
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes.lg};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const WorshipTime = styled.span`
  color: ${theme.colors.secondary};
  font-weight: 600;
  font-size: ${theme.fontSizes.lg};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const InfoSection = styled.div`
  margin-top: ${theme.spacing.xxxl};
  background-color: ${theme.colors.hover};
  padding: ${theme.spacing.xxl};
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${theme.colors.primary};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg};
  }
`;

const InfoTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
  font-family: ${theme.fonts.heading};
`;

const InfoText = styled.p`
  font-size: ${theme.fontSizes.md};
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

export const WorshipGuide: React.FC = () => {
  const worship = CHURCH_INFO.worship;

  return (
    <MainLayout>
      <PageContainer>
        <PageTitle>예배 시간 안내</PageTitle>
        <Subtitle>하나님을 예배하는 귀한 시간에 함께하세요</Subtitle>

        <WorshipTable>
          <TableHead>
            <TableRow>
              <TableHeader>예배명</TableHeader>
              <TableHeader>시간</TableHeader>
              <TableHeader>장소</TableHeader>
            </TableRow>
          </TableHead>
          <tbody>
            {worship.map((service, index) => (
              <TableRow key={index}>
                <TableCell>
                  <WorshipName>{service.name}</WorshipName>
                </TableCell>
                <TableCell>
                  <WorshipTime>{service.time}</WorshipTime>
                </TableCell>
                <TableCell>{service.location}</TableCell>
              </TableRow>
            ))}
          </tbody>
        </WorshipTable>

        <InfoSection>
          <InfoTitle>예배 안내사항</InfoTitle>
          <InfoText>• 주일예배는 오전 9시와 11시, 오후 1시 30분에 드립니다.</InfoText>
          <InfoText>• 새벽기도회는 화요일부터 금요일까지 매일 오전 5시 30분에 드립니다.</InfoText>
          <InfoText>• 주차는 교회 지하주차장을 이용하실 수 있습니다.</InfoText>
        </InfoSection>
      </PageContainer>
    </MainLayout>
  );
};
